import { getCategoryAncestors } from '~/lib/category/ancestors';
import { getCategoryLockStates } from '~/lib/category/lockstate';

export type ContentModal = {
  slug: string;
  post: PostSchema;
  category: CategorySchema;
};

export type UseModalCategory = {
  libraryBookId: string;
  category: Ref<CategorySchema>;
  isLocked: Ref<boolean>;
  ancestors: Ref<CategorySchema[]>;
  contents: ComputedRef<ContentModal[]>;
  content: ComputedRef<ContentModal>;
  contentIndex: ComputedRef<number>;
  children: ComputedRef<PostSchema[]>;
  child: ComputedRef<PostSchema>;
  childIndex: ComputedRef<number>;
  post: ComputedRef<PostSchema>;
  media: Ref<Map<string, MediaSchema>>;
  load: (slug: string) => Promise<void>;
  unload: () => void;
  contentSlug: ComputedRef<string>;
  childSlug: ComputedRef<string>;
  isLoaded: ComputedRef<boolean>;
  isPractice: ComputedRef<boolean>;
  showAsPdf: ComputedRef<boolean>;
  isHeaderNavVisible: ComputedRef<boolean>;
};

export const INJECT_MODAL_CATEGORY = Symbol(
  'useModalCategory composable'
) as InjectionKey<UseModalCategory>;

export const useModalCategory = (
  libraryBookId: string,
  categoryTrialContent: UseCategoryTrialContent
): UseModalCategory => {
  const mediaLoader = useMediaLoader();

  const media = ref<Map<string, MediaSchema>>();

  const {
    category,
    mediaIds,
    fetch,
    unload: unloadCategory,
    isLoaded,
  } = useCategoryPopulate();

  const { contentSlug, childSlug } = useModalRouter();

  const ancestors = ref<CategorySchema[]>([]);

  const profile = useProfileStore();

  const { contentUserTypeFromRoute } = useContentRouteHelper();

  const { currentContent } = useContentsForUserType(
    contentUserTypeFromRoute.value
  );

  const { $db } = useNuxtApp();

  const load = async (slug) => {
    await fetch({ slug, libraryBookId }, false, false, false);
    ancestors.value = await getCategoryAncestors(
      $db,
      category.value,
      libraryBookId
    );
    mediaLoader.addIds(mediaIds);

    media.value = await mediaLoader.retrieveAll();
  };

  const isHeaderNavVisible = computed(() => {
    return [
      ContentTypes.Annals,
      ContentTypes.Essential,
      ContentTypes.Practice,
    ].includes(category.value.contentType);
  });

  const isPractice = computed(() => {
    return category.value?.contentType === 'practice';
  });

  const isLocked = computed(() => {
    const categoryLockState = getCategoryLockStates(
      profile.isASubscriber,
      currentContent.value,
      ancestors.value,
      profile.lockedContent
    );
    let isLocked =
      categoryLockState.getIsTypeLocked(lockedContent.value) ||
      categoryLockState.getIsTypeLocked(firstLockedContent.value);

    const trialType = lockedContent.value || firstLockedContent.value;

    if (isLocked && trialType) {
      isLocked = !categoryTrialContent.isTrialAvailableForTypeAndContentId({
        lockedContent: trialType as TrialTypes,
        id: category.value.id,
      });
    }

    return isLocked;
  });

  const unload = () => {
    unloadCategory();
    ancestors.value = [];
    mediaLoader.releaseIds();
  };

  const contents = computed<ContentModal[]>(() => {
    if (!category.value) return [];
    return [
      ...category.value.posts.map((post) => ({
        slug: post.slug,
        post,
        category: null,
      })),
      ...category.value.children.map((category) => ({
        slug: category.slug,
        post: null,
        category,
      })),
    ];
  });

  const content = computed(() => {
    if (contentSlug.value === '') return contents.value[0];
    return contents.value.find((content) => {
      const slug = content.category ? content.category.slug : content.post.slug;
      return slug === contentSlug.value;
    });
  });

  const showAsPdf = computed(() => {
    if (!content?.value) return false;
    if (!content.value?.post) return false;
    if (content.value.post?.content && isPractice.value) return false;
    return true;
  });

  const contentIndex = computed(() => {
    if (contentSlug.value === '') return 0;
    return contents.value.findIndex((content) => {
      const slug = content.category ? content.category.slug : content.post.slug;
      return slug === contentSlug.value;
    });
  });

  const children = computed(() => {
    return content.value.category
      ? content.value.category.posts
      : [content.value.post];
  });

  const child = computed(() => {
    if (childSlug.value === '') return children.value[0];
    return children.value.find((child) => child.slug === childSlug.value);
  });

  const childIndex = computed(() => {
    if (childSlug.value === '') return 0;
    return children.value.findIndex((child) => child.slug === childSlug.value);
  });

  const firstLockedContent = computed(() => {
    return contents.value[0].post
      ? contents.value[0].post.lockedContent ||
          contents.value[0].post.contentType
      : contents.value[0].category.lockedContent ||
          contents.value[0].category.contentType;
  });

  const lockedContent = computed(
    () => category.value.lockedContent || category.value.contentType
  );

  const post = computed(() => {
    if (child.value) return child.value;
    return content.value.post
      ? content.value.post
      : content.value.category.posts[0];
  });

  return {
    libraryBookId,
    category,
    isLocked,
    contents,
    content,
    contentIndex,
    children,
    child,
    childIndex,
    post,
    media,
    ancestors,
    load,
    unload,
    childSlug,
    contentSlug,
    isLoaded,
    isPractice,
    showAsPdf,
    isHeaderNavVisible,
  };
};
